import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Section } from '@/components/console/Section';
import { cents, fetchOwnerBrief, markBriefOutcome, outcomeFor, pct, runOwnerBriefNow, sourceLabel, type OwnerBrief } from '@/lib/growth-cohorts';
import { NOT_MEASURED, ago, day, num } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * The owner's brief on the Growth page: the five sections the Monday Slack
 * post carries, with Done / Skip on each suggestion. A decision records the
 * cited cohort as it stands today, so the next brief can report what
 * happened after it, on the same rows.
 */
export function BriefSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['owner-brief'], queryFn: fetchOwnerBrief, staleTime: 5 * 60_000 });
  const run = useMutation({ mutationFn: (post: boolean) => runOwnerBriefNow(post), onSuccess: () => qc.invalidateQueries({ queryKey: ['owner-brief'] }) });
  const mark = useMutation({
    mutationFn: (a: { briefId: string; key: string; status: 'done' | 'skipped'; note?: string }) => markBriefOutcome(a.briefId, a.key, a.status, a.note),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner-brief'] }),
  });
  const [note, setNote] = useState<Record<string, string>>({});
  const b: OwnerBrief | null | undefined = q.data;
  const body = b?.brief;

  const head = b ? `week of ${day(b.weekStart)}, generated ${ago(b.generatedAt)}${b.slackStatus ? `, Slack ${b.slackStatus}` : ''}` : q.isLoading ? 'loading' : 'no brief generated yet';

  return (
    <Section title="Owner brief" summary={head} count={body ? body.suggestions.length : null} defaultOpen id="owner-brief">
      <div className={s.filters}>
        <button type="button" className={s.btnGhost} onClick={() => run.mutate(false)} disabled={run.isPending}>{run.isPending ? 'generating' : 'Generate now (no Slack post)'}</button>
        <button type="button" className={s.btnGhost} onClick={() => run.mutate(true)} disabled={run.isPending}>Generate and post to Slack</button>
        {run.isError && <span className={s.error}>Generation failed: {(run.error as Error)?.message ?? 'no detail'}</span>}
        {q.isError && <span className={s.error}>GET /provider/admin/growth/brief did not load.</span>}
      </div>

      {body && (
        <>
          <div className={s.groupTitle} style={{ marginTop: 14 }}>1. What changed this week (prior week)</div>
          <div className={s.card} style={{ padding: 0, overflowX: 'auto' }}>
            <table className={s.table}>
              <thead><tr><th>Measure</th><th style={{ textAlign: 'right' }}>This week</th><th style={{ textAlign: 'right' }}>Prior</th><th>Basis</th></tr></thead>
              <tbody>
                {body.changed.map((c) => (
                  <tr key={c.label}>
                    <td>{c.label}</td>
                    <td style={{ textAlign: 'right' }}>{c.label.startsWith('revenue') ? cents(c.thisWeek) : num(c.thisWeek)}</td>
                    <td style={{ textAlign: 'right' }}>{c.label.startsWith('revenue') ? cents(c.priorWeek) : num(c.priorWeek)}</td>
                    <td className={s.muted} style={{ fontSize: '0.78rem' }}>{c.basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={s.muted} style={{ fontSize: '0.78rem', marginTop: 6 }}>{body.sources.posthog} {body.sources.ledger} {body.sources.founding}</div>

          <div className={s.groupTitle} style={{ marginTop: 14 }}>2. Where people stop (signups from the last 14 days)</div>
          {body.stops.length === 0 ? <div className={s.empty}>Nobody was lost between steps in this cohort.</div> : (
            <ul className={s.list} style={{ margin: 0, paddingLeft: 18 }}>
              {body.stops.map((d) => (
                <li key={d.to} style={{ fontSize: '0.86rem' }}>
                  <strong>{d.from} to {d.to}</strong>: {num(d.lost)} of {num(d.before)} lost{d.lastEvent ? <>; most common last event <code>{d.lastEvent.name}</code> ({d.lastEvent.count})</> : '; no event recorded after the last step'}
                </li>
              ))}
            </ul>
          )}

          <div className={s.groupTitle} style={{ marginTop: 14 }}>3. What is working (top sources by LTV per signup)</div>
          {body.working.length === 0 ? <div className={s.empty}>No source has a measured LTV per signup with a paid person yet.</div> : (
            <ul className={s.list} style={{ margin: 0, paddingLeft: 18 }}>
              {body.working.map((w) => (
                <li key={`${w.by}:${w.sourceKey}`} style={{ fontSize: '0.86rem' }}>
                  <strong>{w.by} {sourceLabel(w.sourceKey)}</strong>: {cents(w.ltvPerSignupCents, w.revenueCurrency)} per signup, n={w.signups}{w.signups < 10 ? ' (small n)' : ''}, paid {w.paid}, day 30 {w.eligibleD30 ? pct(w.retentionD30, w.eligibleD30) : 'no one past day 30 yet'}
                </li>
              ))}
            </ul>
          )}

          <div className={s.groupTitle} style={{ marginTop: 14 }}>4. Suggested actions</div>
          {body.suggestions.length === 0 && <div className={s.empty}>No rule fired: nothing in the rows stands out this week.</div>}
          {body.suggestions.map((sg, i) => {
            const decided = b ? outcomeFor(b, sg.suggestionKey) : null;
            return (
              <div key={sg.suggestionKey} className={s.card} style={{ marginBottom: 8 }} data-rule={sg.ruleId}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span className={s.proposalN}>{i + 1}</span>
                  <strong style={{ flex: '1 1 300px' }}>{sg.action}</strong>
                  <span className={s.pill}>{sg.ruleId}</span>
                  {decided && <span className={`${s.pill} ${decided.status === 'done' ? s.pill_praise : ''}`}>{decided.status} {ago(decided.decidedAt)}</span>}
                </div>
                <dl className={s.facts} style={{ marginTop: 8 }}>
                  <dt>Why</dt><dd>{sg.why.join(' ')}</dd>
                  <dt>Expected</dt><dd>{sg.expectedEffect}</dd>
                  <dt>Downside</dt><dd>{sg.downside}</dd>
                  <dt>Measure</dt><dd>{sg.measure}</dd>
                  <dt>Rows</dt><dd className={s.muted}>{sg.cited.map((c) => `${c.table} ${c.key}`).join('; ')}</dd>
                </dl>
                {!decided && b && (
                  <div className={s.filters} style={{ marginTop: 8 }}>
                    <input className={s.search} value={note[sg.suggestionKey] ?? ''} onChange={(e) => setNote({ ...note, [sg.suggestionKey]: e.target.value })} placeholder="Note (optional): what you did" aria-label="Decision note" />
                    <button type="button" className={s.btn} disabled={mark.isPending} onClick={() => mark.mutate({ briefId: b.id, key: sg.suggestionKey, status: 'done', note: note[sg.suggestionKey] })}>Done</button>
                    <button type="button" className={s.btnGhost} disabled={mark.isPending} onClick={() => mark.mutate({ briefId: b.id, key: sg.suggestionKey, status: 'skipped', note: note[sg.suggestionKey] })}>Skip</button>
                  </div>
                )}
                {decided?.note && <div className={s.muted} style={{ fontSize: '0.8rem', marginTop: 6 }}>Note: {decided.note}</div>}
              </div>
            );
          })}
          {mark.isError && <div className={s.error}>Could not record the decision: {(mark.error as Error)?.message ?? 'no detail'}</div>}

          <div className={s.groupTitle} style={{ marginTop: 14 }}>5. What happened after earlier decisions</div>
          {body.outcomes.length === 0 ? <div className={s.empty}>No decision has been marked done or skipped before this brief. Mark one above; the next brief measures the cited cohort again.</div> : (
            <ul className={s.list} style={{ margin: 0, paddingLeft: 18 }}>
              {body.outcomes.map((o) => <li key={o.suggestionKey} style={{ fontSize: '0.86rem' }}>{o.line}</li>)}
            </ul>
          )}
        </>
      )}
      {!body && !q.isLoading && !q.isError && <div className={s.empty}>{NOT_MEASURED}: generate the first brief above. The scheduled one posts Monday 08:00 Denver once OWNER_BRIEF_ENABLED is on.</div>}
    </Section>
  );
}
